'use client';

import { useCallback } from 'react';
import { PositionDirection, PublicKey } from '@velocity-exchange/sdk';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useHandleTxChain } from 'src/hooks/driftActions/transactions/useHandleTxChain';
import { useGetMakerInfoForMarketOrder } from './useGetMakerInfoForMarketOrder';
import { dlog } from 'src/dev';

/**
 * Hook for filling orders
 */
export const useFillOrder = () => {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const currentUserAccount = useCurrentUserAccount();

	const getTxParams = useGetTxParams();
	const { handleTxChain } = useHandleTxChain();
	const { getMakerInfoForMarketOrder } = useGetMakerInfoForMarketOrder();

	const fillOrder = useCallback(
		async (
			orderId: number,
			marketIndex: number,
			direction: PositionDirection
		) => {
			if (!driftClient || !walletAdapter?.publicKey || !currentUserAccount) {
				return;
			}

			try {
				const makers = await getMakerInfoForMarketOrder({
					direction,
					marketIndex,
				});

				// TODO - needs sdk refactor to work for spot orders
				const userAccountData = currentUserAccount.client?.getUserAccount();
				if (!userAccountData) return false;

				const txSig = await handleTxChain(
					driftClient.fillPerpOrder(
						currentUserAccount.pubKey as PublicKey,
						userAccountData,
						{
							marketIndex,
							orderId,
						},
						makers ?? undefined,
						getTxParams(),
						0,
						walletAdapter.publicKey
					)
				);

				dlog('fill_order_tx', txSig);

				notify({
					type: 'success',
					message: 'Filled order.',
				});
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					wallet: walletAdapter,
				});
			}
		},
		[
			driftClient,
			walletAdapter,
			currentUserAccount,
			getTxParams,
			handleTxChain,
			getMakerInfoForMarketOrder,
		]
	);

	return { fillOrder };
};
